import clsx from "clsx";
import { FC, PropsWithChildren, useMemo, useState } from "react";
import { MsgMulti } from "rujira.js";
import { Toggle, useTranslation } from "rujira.ui";

import { MsgProvider, useMsg } from "../../common/components/TxButton";
import { useAccounts } from "../../services/accounts.tsx";
import { TRADE_AUTO_CLAIM_ENABLED } from "../features";
import { useAutoClaimer } from "../hooks/useAutoClaimer.tsx";

export const AutoClaimerToggle: FC<PropsWithChildren> = ({ children }) => {
  if (!TRADE_AUTO_CLAIM_ENABLED) return children;

  return <EnabledAutoClaimerToggle>{children}</EnabledAutoClaimerToggle>;
};

const EnabledAutoClaimerToggle: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { selected } = useAccounts();
  const [autoclaimEnabled, setAutoclaimEnabled] = useState(false);

  const { msgs, activeInstance, error, unsupported } = useAutoClaimer({
    appId: "RUJI-Trade",
    enabled: autoclaimEnabled,
  });

  const orderMsg = useMsg();
  const isLoading = msgs === "loading";

  const msg = useMemo(() => {
    if (!orderMsg) return null;
    if (
      autoclaimEnabled &&
      (unsupported || error || msgs === "loading" || !msgs.length)
    )
      return null;
    if (msgs === "loading") return null;
    return autoclaimEnabled && msgs.length > 0
      ? new MsgMulti(orderMsg.account, [orderMsg, ...msgs])
      : orderMsg;
  }, [orderMsg, autoclaimEnabled, msgs, activeInstance, error, unsupported]);

  if (activeInstance) return children;

  return (
    <>
      <div className="flex ai-c jc-e mt-2 gap-0.5">
        <span
          className="color-grey fs-12 tooltip-label"
          data-tooltip-content={
            unsupported
              ? t("autoClaimUnavailableForAccount")
              : t("autoClaimTooltip")
          }
          data-tooltip-id="global-tip"
          data-tooltip-place="top">
          {t("autoClaim")}
        </span>
        <Toggle
          className={clsx("toggle--xs", {
            "is-loading": isLoading,
          })}
          checked={autoclaimEnabled}
          onChange={() => setAutoclaimEnabled(!autoclaimEnabled)}
          disabled={!selected || (unsupported && !autoclaimEnabled)}
        />
      </div>
      {autoclaimEnabled && error && (
        <p className="fs-12 color-grey" role="alert">
          {t("automationUnavailable")}
        </p>
      )}
      <MsgProvider msg={msg}>{children}</MsgProvider>
    </>
  );
};
